import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.haythix.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/social-card.png`;
const SITE_NAME = 'haythix';
// Default description is used when a page does not supply its own `description`
// prop. The "147 tools" number must stay in sync with PLATFORM_FEATURE_COUNT
// (which derives from ComparisonPage's SECTIONS) — when the catalog grows,
// update this string. The build script `scripts/count-features.mjs` reports the
// current truth.
const DEFAULT_DESCRIPTION =
  'haythix is the verifiable-methodology crypto trading platform. Multi-chain DEX + CEX trading bots, copy trading, cross-chain arbitrage, and 147 tools across 10 categories — every claim backed by a public API.';

/**
 * Seo — per-route head injection (title, description, OG, Twitter, canonical, JSON-LD).
 *
 * Pass title + description per page. Everything else falls back to sensible defaults.
 * Pass `jsonLd` (object or array of objects) to inject structured data — Google reads
 * JSON-LD for rich results (SoftwareApplication, FAQPage, Article, BreadcrumbList).
 *
 * Conventions:
 *   - title       — page-specific (50-60 chars). Suffix " — haythix" is auto-appended.
 *   - description — page-specific (140-160 chars).
 *   - path        — canonical path beginning with /. Used to compute canonical + og:url.
 *   - image       — absolute URL or path. Defaults to /social-card.png.
 *   - jsonLd      — object or array. Stringified into a <script type="application/ld+json">.
 *   - noIndex     — when true, emits <meta name="robots" content="noindex,nofollow">.
 */
export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image,
  jsonLd,
  noIndex = false,
  type = 'website',
}) {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — verifiable-methodology crypto trading`;
  const canonical = `${SITE_URL}${path}`;
  const ogImage = image
    ? (image.startsWith('http') ? image : `${SITE_URL}${image.startsWith('/') ? '' : '/'}${image}`)
    : DEFAULT_OG_IMAGE;

  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {jsonLdArray.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
}

export { SITE_URL, SITE_NAME };
